import React, {useState} from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import {getParsedSessionOrderType} from "../../../../../utils/order";
import AvailableHelper from "../../../../../services/Menu/services/Availability/AvailableHelper";
import DateService from "../../../../../services/Common/Date/DateService";
import HoursHelper from "../../../../../services/Menu/services/Availability/HoursHelper";
import ItemHelper from "../../../../../services/Menu/services/Item/ItemHelper";
import ItemViewHelper from "../../../../../services/Menu/services/Item/ItemViewHelper";
import {OrderType} from "../../../../../services/Api/types";
import {DateHelper} from "../../../../../services/Common/Date/DateHelper";
import {createKeyDownHandler} from "../../../../../utils/keyDownHanlder";
import {isAnotherDateAvailable} from "../../../../../components/Cart/utils";
import ChangePickDateDrawer from "../../../../../components/Drawer/ChangePickDateDrawer";
import DisabledCartItemsModal
    from "../../../../../components/Cart/components/DisabledCartItemsModal/DisabledCartItemsModal";
import {RootState} from "../../../../../../reducers";
import {getAvailabilities, getItems, getMenus, getPromotions} from "../../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../../services/Session/reducer";
import {getStoreConfig} from "../../../../../services/Store/reducer";
import {getCart} from "../../../../../services/Cart/reducer";
import {connect} from "react-redux";
import {ReactComponent as BasketIco} from "../../../../../../images/icons/basket-fast.svg";
import {ReactComponent as CalendarIco} from "../../../../../../images/icons/calendar.svg";
import {ReactComponent as EditIco} from "../../../../../../images/icons/edit.svg";

interface OwnProps {
    time: string | undefined;
    onChange: (time: string | undefined) => void;
    currentType: OrderType | undefined;
    isAvailable: boolean;
    availabilityInfo: {
        text: string
        placement: "bottom" | "top"
        nearestDate?: Date | undefined
    } | undefined
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductChangeOrderDetailsTimeModal = ({
                                                time,
                                                onChange,
                                                locale,
                                                availabilities,
                                                menus,
                                                currentType,
                                                config,
                                                promotions,
                                                cart,
                                                items,
                                                session,
                                                availabilityInfo,
                                                isAvailable,
                                            }: Props) => {
    const {t} = useTranslation();
    const parsedCurrentType = getParsedSessionOrderType(currentType);
    const [showPickDateDrawer, setShowPickDateDrawer] = useState(false);
    const orderTypeMenuDateAvailabilities = AvailableHelper.geDateAvailableAvailabilities(
        availabilities,
        menus.map((menu: any) => parsedCurrentType && menu[parsedCurrentType.toLowerCase()] && menu.availability_id),
        DateService.emptyOrNow(time)
    );
    const [disabledCartItems, setDisabledCartItems] = useState<any[]>([]);
    const [lastTime, setLastTime] = useState<string | undefined>(time);
    const anotherDateAvailable = isAnotherDateAvailable(config, menus, parsedCurrentType);
    const [possibleNearestDateSelected, setPossibleNearestDateSelected] = useState(false);

    const getPossibleDisabledItemsForTime = (date: string | undefined) => {
        const isOpen = HoursHelper.isOpen(config?.hours || [], DateService.emptyOrNow(date));

        if (!isOpen) {
            return cart.items.map((cartItem: any) => ItemHelper.getItem(items, cartItem.item_id)).filter(Boolean);
        }

        const cartItems = cart.items.map((cartItem: any) => {

            const item = ItemHelper.getItem(items, cartItem.item_id);

            if (!item) return undefined;

            return {
                item,
                itemView: ItemViewHelper.parseItem(item, availabilities, promotions, date, undefined, cart, currentType as OrderType, {
                    skipType: true
                })
            }
        }).filter(Boolean);

        return cartItems.filter(item => item?.itemView?.disabled || !HoursHelper.isOpen(orderTypeMenuDateAvailabilities.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(date))).map(item => item?.item).filter(Boolean);
    }

    const handleSaveDate = (date: string, skipCheckingItems?: true) => {
        setLastTime(date);

        if (!skipCheckingItems) {
            const possibleDisabledItems = getPossibleDisabledItemsForTime(date);
            if (possibleDisabledItems.length > 0) {
                setDisabledCartItems(possibleDisabledItems);
                return;
            }
        }

        onChange(date);
        setShowPickDateDrawer(false);
    }

    const handleAsSoonAsPossibleOptionChange = () => {
        if (!isAvailable) return;

        setLastTime(undefined);

        const possibleDisabledItems = getPossibleDisabledItemsForTime(undefined);
        if (possibleDisabledItems.length > 0) {
            setDisabledCartItems(possibleDisabledItems);
            return;
        }

        onChange(undefined);
    }

    const handleOnLabelKeyDown = (time: boolean) => createKeyDownHandler<HTMLLabelElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: () => {
            if (time) {
                setShowPickDateDrawer(true);
            } else {
                handleAsSoonAsPossibleOptionChange()
            }
        }
    });

    const handleOnHideModal = () => {
        setDisabledCartItems([]);
    }

    const handleOnSubmitModal = () => {
        if (lastTime) {
            handleSaveDate(lastTime, true);
        } else {
            onChange(undefined);
        }
        handleOnHideModal();
    }

    const handleSetNearestAvailableDate = () => {
        const dateString = DateHelper.format(DateHelper.roundDateTimeToQuarter(availabilityInfo?.nearestDate!));
        setLastTime(dateString);
        onChange(dateString);
        setPossibleNearestDateSelected(true);
    }

    return (
        <>
            <ChangePickDateDrawer pickDate={time}
                                  showDrawer={showPickDateDrawer}
                                  handleSaveDate={handleSaveDate}
                                  onHide={() => setShowPickDateDrawer(false)}
                                  higherZIndex
                                  withoutAsSoonAsPossibleOption
                                  orderType={currentType}/>
            {disabledCartItems.length > 0 && <DisabledCartItemsModal show={disabledCartItems.length > 0}
                                                                     items={disabledCartItems}
                                                                     type="PICKUP_AT"
                                                                     pickupAt={lastTime}
                                                                     onSubmit={handleOnSubmitModal}
                                                                     onHide={handleOnHideModal}/>}
            <div
                className={`${availabilityInfo?.placement === "top" ? "mt-2 " : "mt-4 "} d-flex flex-column gap-2 change-order-details-time`}>
                {
                    availabilityInfo?.placement === "top" ? (
                        <>
                            {
                                !availabilityInfo?.nearestDate && !anotherDateAvailable ? <></> : (
                                    <>
                                        <button className="btn btn-lg btn-primary"
                                                disabled={!availabilityInfo?.nearestDate}
                                                onClick={handleSetNearestAvailableDate}>
                                            {t("modules.product.action.set_nearest_available_date.title")}
                                        </button>
                                        {anotherDateAvailable && (
                                            <button className="btn btn-lg btn-default"
                                                    onClick={() => setShowPickDateDrawer(true)}>
                                                {t("modules.product.action.choose_another_date.title")}
                                            </button>
                                        )}
                                    </>
                                )
                            }
                        </>
                    ) : (
                        <>
                            <h1 className="h5 mb-2">{t("modules.cart.field.pickup_date.title")}</h1>
                            {
                                !possibleNearestDateSelected && (
                                    <label tabIndex={0} aria-checked={!time} onKeyDown={handleOnLabelKeyDown(false)}
                                           className={`btn btn-default btn-lg btn-check-full ${!time ? " active" : ""} ${!isAvailable ? "disabled" : ""}`}>
                                        <BasketIco/>
                                        <span>
                                    {t("common.word.as_soon_as_possible")}
                                </span>
                                        <input className="btn-check1" type="radio" name="time" value={undefined}
                                               autoComplete="off"
                                               tabIndex={-1}
                                               checked={!time} onChange={() => handleAsSoonAsPossibleOptionChange()}/>
                                    </label>
                                )
                            }
                            {anotherDateAvailable && (
                                <label tabIndex={0} aria-checked={!time} onKeyDown={handleOnLabelKeyDown(true)}
                                       className={`btn btn-default btn-lg btn-check-full ${time ? " active" : ""}`}>
                                    <CalendarIco/>
                                    <span>
                                        <p className="mb-0">{t("modules.cart.field.another_date.title")}</p>
                                        {time &&
                                            <small
                                                className="text-primary">{DateHelper.getFormattedNearestDate(time, t, locale ?? "")}</small>}
                                    </span>
                                    <input style={{width: !!time ? 0 : "unset"}} className="btn-check1" type="radio"
                                           name="time"
                                           autoComplete="off"
                                           tabIndex={-1}
                                           checked={!!time} onChange={() => setShowPickDateDrawer(true)}
                                           onClick={() => setShowPickDateDrawer(true)}/>
                                    {
                                        !!time && (
                                            <EditIco className="text-primary mx-0"/>
                                        )
                                    }
                                </label>
                            )}
                            {
                                !anotherDateAvailable && session.pickup_at && (
                                    <label tabIndex={0} aria-checked={!time}
                                           className={`btn btn-default btn-lg btn-check-full ${time ? " active" : ""} ${!time ? "disabled" : ""}`}>
                                        <CalendarIco/>
                                        <span>
                                            <p className="mb-0">{t("modules.cart.field.another_date.title")}</p>
                                            <small className="text-danger">
                                                {t("common.word.not_available")}
                                            </small>
                                        </span>
                                        <input className="btn-check1" type="radio" name="time"
                                               autoComplete="off"
                                               tabIndex={-1}
                                               checked={!!time}
                                        />
                                    </label>
                                )
                            }
                        </>
                    )
                }
            </div>
        </>
    )
}

const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    cart: getCart(state.cart),
    promotions: getPromotions(state.menu),
    items: getItems(state.menu),
    menus: getMenus(state.menu),
    session: getSession(state.session)
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductChangeOrderDetailsTimeModal));
